import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchSalesStatsAction } from "../../../redux/slices/orders/ordersSlices";
import LoadingComponent from "../../LoadingComp/LoadingComponent";
import ErrorMsg from "../../ErrorMessage/ErrorMsg";

export default function OrdersStatistics() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const hasFetched = useRef(false); // Use ref to prevent multiple fetches
    
    const { salesStats, loading, error } = useSelector((state) => state?.orders || {});
    
    // Only fetch once when component mounts
    useEffect(() => {
        if (!hasFetched.current && !salesStats && !loading) {
            console.log("Fetching sales stats once...");
            hasFetched.current = true;
            dispatch(fetchSalesStatsAction());
        }
    }, [dispatch, salesStats, loading]);
    
    // Show loading state
    if (loading && !salesStats) {
        return (
            <div className="flex justify-center items-center h-32">
                <LoadingComponent />
            </div>
        );
    }
    
    // Show error state
    if (error && !salesStats) {
        return (
            <div className="mb-6">
                <ErrorMsg message={error?.message || t('orders.errorLoadingStats')} />
                <button
                    onClick={() => {
                        hasFetched.current = false;
                        dispatch(fetchSalesStatsAction());
                    }}
                    className="mt-2 text-sm text-orange-600 hover:text-orange-700"
                >
                    {t('common.retry')}
                </button>
            </div>
        );
    }
    
    // Show nothing if no stats yet
    if (!salesStats) {
        return null;
    }
    
    const stats = salesStats;
    
    return (
        <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('orders.salesStatistics')}</h2>
            
            {/* First Row - Key Metrics */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                </svg>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">{t('orders.totalOrders')}</dt>
                                    <dd className="text-lg font-semibold text-gray-900">{stats.totalOrders || 0}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">{t('orders.totalRevenue')}</dt>
                                    <dd className="text-lg font-semibold text-gray-900">${(stats.totalRevenue || 0).toFixed(2)}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">{t('orders.paidRevenue')}</dt>
                                    <dd className="text-lg font-semibold text-green-600">${(stats.paidRevenue || 0).toFixed(2)}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                </svg>
                            </div>
                            <div className="ml-5 w-0 flex-1">
                                <dl>
                                    <dt className="text-sm font-medium text-gray-500 truncate">{t('orders.averageOrderValue')}</dt>
                                    <dd className="text-lg font-semibold text-gray-900">${(stats.averageOrderValue || 0).toFixed(2)}</dd>
                                </dl>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Second Row - Time-based Metrics */}
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mt-5">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">{t('orders.todayOrders')}</dt>
                            <dd className="text-lg font-semibold text-gray-900">{stats.todayOrders || 0}</dd>
                            <dd className="text-sm text-gray-500 mt-1">${(stats.todayRevenue || 0).toFixed(2)}</dd>
                        </dl>
                    </div>
                </div>
                
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">{t('orders.thisMonthOrders')}</dt>
                            <dd className="text-lg font-semibold text-gray-900">{stats.thisMonthOrders || 0}</dd>
                            <dd className="text-sm text-gray-500 mt-1">${(stats.thisMonthRevenue || 0).toFixed(2)}</dd>
                        </dl>
                    </div>
                </div>
                
                <div className="bg-white overflow-hidden shadow rounded-lg">
                    <div className="p-5">
                        <dl>
                            <dt className="text-sm font-medium text-gray-500 truncate">{t('orders.orderStatus')}</dt>
                            <dd className="text-sm text-gray-900 mt-2">
                                <div className="flex justify-between">
                                    <span>{t('orders.pending')}:</span>
                                    <span className="font-semibold">{stats.ordersByStatus?.pending || 0}</span>
                                </div>
                                <div className="flex justify-between mt-1">
                                    <span>{t('orders.confirmed')}:</span>
                                    <span className="font-semibold">{stats.ordersByStatus?.confirmed || 0}</span>
                                </div>
                                <div className="flex justify-between mt-1">
                                    <span>{t('orders.shipped')}:</span>
                                    <span className="font-semibold">{stats.ordersByStatus?.shipped || 0}</span>
                                </div>
                                <div className="flex justify-between mt-1">
                                    <span>{t('orders.delivered')}:</span>
                                    <span className="font-semibold">{stats.ordersByStatus?.delivered || 0}</span>
                                </div>
                                <div className="flex justify-between mt-1">
                                    <span>{t('orders.cancelled')}:</span>
                                    <span className="font-semibold text-red-600">{stats.ordersByStatus?.cancelled || 0}</span>
                                </div>
                            </dd>
                        </dl>
                    </div>
                </div>
            </div>
        </div>
    );
}